import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.email, form.password, form.name);
      toast.success(mode === "login" ? "Connexion réussie" : "Bienvenue au Fil");
      const dest = user.role === "admin" ? "/admin" : (loc.state?.from || "/espace");
      nav(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-32 pb-20 px-6 md:px-12 grid md:grid-cols-2 items-center gap-16 max-w-[1400px] mx-auto" data-testid="login-page">
      <div className="hidden md:block relative aspect-square overflow-hidden border border-fg/15">
        <img src="https://images.unsplash.com/photo-1645390841042-77720e06e2b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Canal" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-fg/40" />
        <div className="absolute bottom-8 left-8 right-8 text-bg">
          <div className="meta-label text-cinabre">§ Membre du Fil</div>
          <h2 className="font-serif text-5xl leading-[0.95] tracking-tight mt-4">Rejoindre l'aventure.</h2>
        </div>
      </div>

      <div>
        <div className="meta-label text-cinabre">§ {mode === "login" ? "Connexion" : "Créer un compte"}</div>
        <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mt-4">
          {mode === "login" ? "Retour au Fil." : "Bienvenue au Fil."}
        </h1>

        <form onSubmit={submit} className="mt-12" data-testid="auth-form">
          {mode === "register" && (
            <div className="mb-8">
              <label className="meta-label text-fg/60 block mb-2">Nom</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-2xl"
                data-testid="auth-name" />
            </div>
          )}
          <div className="mb-8">
            <label className="meta-label text-fg/60 block mb-2">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-2xl"
              data-testid="auth-email" />
          </div>
          <div className="mb-10">
            <label className="meta-label text-fg/60 block mb-2">Mot de passe *</label>
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-2xl"
              data-testid="auth-password" />
          </div>
          <button disabled={loading} className="btn-editorial" data-testid="auth-submit">
            {loading ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
          </button>

          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="block mt-6 link-underline font-mono text-[11px] tracking-[0.2em] uppercase text-fg/70"
            data-testid="auth-switch">
            {mode === "login" ? "Pas encore membre ? Créer un compte" : "Déjà membre ? Se connecter"}
          </button>
        </form>

        <Link to="/" className="block mt-10 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/50 link-underline">← Retour au manifeste</Link>
      </div>
    </main>
  );
}
